import {ConsecutiveCAPI401sProvider} from '@github-ui/agent-sessions/contexts/ConsecutiveCAPI401sContext'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {AlertIcon} from '@primer/octicons-react'
import {Banner, Link, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {getDisplayFilePaths, sortFindingsBySeverity} from './entity-helpers'
import {Finding} from './Finding'
import {FindingsFilterBar} from './FindingsFilterBar'
import {FixFindingDialog} from './FixFindingDialog'
import type {Finding as FindingType, FindingResolvedReason} from './types'
import {useExpandedFindings} from './use-expanded-findings'
import {useFilterFindings} from './use-filter-findings'
import {useFindings} from './use-findings'
import {useIgnoreFindings} from './use-ignore-findings'

interface FindingsProps {
  pullRequestBasePath: string
}

export const Findings: FC<FindingsProps> = props => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  return (
    <ErrorBoundary
      boundaryName="Findings"
      onError={e => setErrorMessage(e.message)}
      fallback={<FindingsError error={errorMessage} />}
    >
      <ConsecutiveCAPI401sProvider>
        <FindingsInner {...props} />
      </ConsecutiveCAPI401sProvider>
    </ErrorBoundary>
  )
}

interface FindingsErrorProps {
  error?: string | null
}

const FindingsError: FC<FindingsErrorProps> = ({error}) => (
  <Blankslate className="border rounded-3" role="alert">
    <Blankslate.Visual>
      <AlertIcon size="medium" className="color-fg-danger" />
    </Blankslate.Visual>
    <Blankslate.Heading>Failed to load findings</Blankslate.Heading>
    {error && <Blankslate.Description>{error}</Blankslate.Description>}
  </Blankslate>
)

const FindingsInner: FC<FindingsProps> = ({pullRequestBasePath}) => {
  const {findings, freshness, modelsUrl, isLoading, error, isNotFound} = useFindings({
    pullRequestBasePath,
  })

  const {openFindings, ignoredFindings, handleDismiss, handleReopen, actionError} = useIgnoreFindings(
    findings,
    pullRequestBasePath,
  )

  const {displayedFindings, ...filterProps} = useFilterFindings(openFindings, ignoredFindings, pullRequestBasePath)

  const [fixFindings, setFixFindings] = useState<FindingType[] | null>(null)
  const {expandedFindingIdSet, handleToggleFindingExpanded} = useExpandedFindings(pullRequestBasePath)

  // handling optimistic updates for fix tasks
  const [fixCcaTaskIds, setFixCcaTaskIds] = useState<Record<string, string>>({})
  const handleTaskCreated = useCallback((ccaTaskId: string, _taskUrl: string, findingIds: string[]) => {
    setFixCcaTaskIds(prev => {
      const next = {...prev}
      for (const id of findingIds) next[id] = ccaTaskId
      return next
    })
  }, [])

  // Announce when findings finish processing (freshness transitions to 'current')
  const previousFreshnessRef = useRef(freshness)
  useEffect(() => {
    if (previousFreshnessRef.current !== 'current' && freshness === 'current') {
      announce('Findings updated')
    }
    previousFreshnessRef.current = freshness
  }, [freshness])

  // Announce when no findings are available (so screen reader users know loading finished)
  useEffect(() => {
    if (isNotFound) {
      announce('No findings available')
    }
  }, [isNotFound])

  if (isLoading) {
    return (
      <div className="d-flex flex-justify-center tmp-p-6" role="status" aria-label="Loading findings">
        <Spinner size="large" />
      </div>
    )
  }

  if (isNotFound) {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>No findings available</Blankslate.Heading>
        <Blankslate.Description>
          Copilot has not detected any findings for this pull request. This could be because the analysis has not
          completed yet, or because there are no findings to report.
        </Blankslate.Description>
      </Blankslate>
    )
  }

  if (error) {
    return <FindingsError error={error} />
  }

  if (freshness === 'pending') {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>Processing findings</Blankslate.Heading>
        <Blankslate.Description>
          Findings are being processed. This page will update automatically
        </Blankslate.Description>
      </Blankslate>
    )
  }

  if (openFindings.length === 0 && ignoredFindings.length === 0) {
    return (
      <Blankslate className="border rounded-3">
        <Blankslate.Heading>No findings</Blankslate.Heading>
        <Blankslate.Description>There are no findings for this pull request</Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <section aria-labelledby="findings-heading" className="d-flex flex-column width-full">
      {freshness === 'stale' && (
        <Banner variant="info" title="Updating findings" className="tmp-mb-3">
          New findings are being processed. This page will update automatically.
        </Banner>
      )}
      {actionError && (
        <div role="alert">
          <Banner variant="warning" title="Action failed">
            {actionError}
          </Banner>
        </div>
      )}
      <FindingsFilterBar
        handleFix={() => setFixFindings(displayedFindings)}
        handleReopen={() => handleReopen(displayedFindings)}
        handleDismiss={() => handleDismiss(displayedFindings)}
        {...filterProps}
      />
      <FindingsList
        displayedFindings={displayedFindings}
        selectedView={filterProps.selectedView}
        expandedFindingIds={expandedFindingIdSet}
        fixCcaTaskIds={fixCcaTaskIds}
        pullRequestBasePath={pullRequestBasePath}
        onToggleExpanded={handleToggleFindingExpanded}
        onDismiss={(finding, resolvedReason) => handleDismiss([finding], resolvedReason)}
        onReopen={finding => handleReopen([finding])}
        onFix={finding => setFixFindings([finding])}
      />
      {fixFindings && (
        <FixFindingDialog
          findings={fixFindings}
          basePath={pullRequestBasePath}
          modelsUrl={modelsUrl}
          onClose={() => setFixFindings(null)}
          onTaskCreated={handleTaskCreated}
        />
      )}
    </section>
  )
}

interface FindingsListProps {
  displayedFindings: FindingType[]
  selectedView: 'attention' | 'open' | 'resolved'
  expandedFindingIds: ReadonlySet<string>
  fixCcaTaskIds: Record<string, string>
  pullRequestBasePath: string
  onToggleExpanded: (findingId: string) => void
  onDismiss: (finding: FindingType, resolvedReason: FindingResolvedReason) => void
  onReopen: (finding: FindingType) => void
  onFix: (finding: FindingType) => void
}

const FindingsList: FC<FindingsListProps> = ({
  displayedFindings,
  selectedView,
  expandedFindingIds,
  fixCcaTaskIds,
  pullRequestBasePath,
  onToggleExpanded,
  onDismiss,
  onReopen,
  onFix,
}) => {
  const sorted = useMemo(() => sortFindingsBySeverity(displayedFindings), [displayedFindings])
  const displayFilePaths = useMemo(
    () => getDisplayFilePaths(sorted.filter(finding => finding.fileUrl).map(finding => finding.filePath)),
    [sorted],
  )

  const resolved = selectedView === 'resolved'
  const taskBasePath = pullRequestBasePath.replace(/\/pull\/.*$/, '')
  const feedbackPath = `${pullRequestBasePath}/code_review_findings/feedback`

  if (displayedFindings.length === 0) {
    return (
      <>
        <div role="status" className="sr-only">
          No findings displayed
        </div>
        <Blankslate className="border rounded-3">
          <Blankslate.Heading>
            {selectedView === 'attention'
              ? 'No findings need your attention'
              : selectedView === 'open'
                ? 'No other findings'
                : 'No resolved findings'}
          </Blankslate.Heading>
          <Blankslate.Description>
            {selectedView === 'attention'
              ? 'There are no critical or high severity findings for this pull request'
              : selectedView === 'open'
                ? 'There are no low or medium severity findings for this pull request'
                : 'There are no resolved findings for this pull request'}
          </Blankslate.Description>
        </Blankslate>
      </>
    )
  }

  return (
    <>
      <div role="status" className="sr-only">
        {`Showing ${sorted.length} ${sorted.length === 1 ? 'finding' : 'findings'}`}
      </div>
      <ul aria-label="Findings">
        {sorted.map(finding => (
          <Finding
            key={finding.id}
            finding={finding}
            displayFilePath={displayFilePaths.get(finding.filePath)}
            resolved={resolved}
            isExpanded={expandedFindingIds.has(finding.id)}
            ccaTaskId={fixCcaTaskIds[finding.id] ?? finding.ccaTaskId ?? undefined}
            feedbackPath={feedbackPath}
            taskBasePath={taskBasePath}
            onToggleExpanded={() => onToggleExpanded(finding.id)}
            onDismiss={resolvedReason => onDismiss(finding, resolvedReason)}
            onReopen={() => onReopen(finding)}
            onFix={() => onFix(finding)}
          />
        ))}
      </ul>
      <p className="text-center color-fg-muted text-small mt-3">
        <Link
          className="Link--inTextBlock"
          href="https://docs.github.com/en/copilot/responsible-use-of-github-copilot-features/responsible-use-of-github-copilot-code-review"
          inline
          muted
        >
          Copilot
        </Link>
        {' uses AI. Check for mistakes.'}
      </p>
    </>
  )
}
